import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes, ReactNode } from "react";
import { useId } from "react";

export interface FieldProps {
  label?: string;
  error?: string;
  hint?: string;
  children: (id: string) => ReactNode;
}

export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  return (
    <label className="ui-field" htmlFor={id}>
      {label ? <span className="ui-field__label">{label}</span> : null}
      {children(id)}
      {hint ? <span className="ui-field__hint">{hint}</span> : null}
      {error ? <span className="ui-field__error">{error}</span> : null}
    </label>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  mono?: boolean;
}

export function TextInput({ label, error, hint, mono, className, ...rest }: TextInputProps) {
  const cls = ["ui-input", mono ? "ui-input--mono" : "", error ? "ui-input--invalid" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  if (!label && !error && !hint) return <input className={cls} {...rest} />;
  return (
    <Field label={label} error={error} hint={hint}>
      {(id) => <input id={id} className={cls} {...rest} />}
    </Field>
  );
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
}

export function SelectField({ label, error, hint, options, className, ...rest }: SelectFieldProps) {
  const cls = ["ui-select", error ? "ui-select--invalid" : "", className ?? ""].filter(Boolean).join(" ");
  const select = (id?: string) => (
    <select id={id} className={cls} {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
  if (!label && !error && !hint) return select();
  return (
    <Field label={label} error={error} hint={hint}>
      {(id) => select(id)}
    </Field>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  mono?: boolean;
}

export function TextArea({ label, error, hint, mono, className, ...rest }: TextAreaProps) {
  const cls = ["ui-textarea", mono ? "ui-input--mono" : "", className ?? ""].filter(Boolean).join(" ");
  if (!label && !error && !hint) return <textarea className={cls} {...rest} />;
  return (
    <Field label={label} error={error} hint={hint}>
      {(id) => <textarea id={id} className={cls} {...rest} />}
    </Field>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  error?: string;
}

export function Checkbox({ label, error, ...rest }: CheckboxProps) {
  return (
    <div>
      <label className="ui-checkbox">
        <input type="checkbox" {...rest} />
        <span>{label}</span>
      </label>
      {error ? <span className="ui-field__error">{error}</span> : null}
    </div>
  );
}
